import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { WEB_ROUTES } from '@portal-contract';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { api } from '@/api/client';
import { useSession } from '@/session/SessionProvider';
import { PageHeading } from '@/components/PageHeading';

/** `/unavailable`: the kill switch is on. Shows the staff message in the current language and lets the visitor re-check. */
export function UnavailableScreen() {
  const { t, lang } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<{ en: string; es: string } | null>(null);
  const [checking, setChecking] = useState(false);

  // A direct visit (or a reload) has no message from an API error: ask the public status endpoint.
  useEffect(() => {
    const ctl = new AbortController();
    api
      .status(ctl.signal)
      .then((s) => setFetched(s.message))
      .catch(() => {});
    return () => ctl.abort();
  }, []);

  const message = (session.unavailableMessage ?? fetched)?.[lang] ?? null;

  async function recheck() {
    setChecking(true);
    try {
      const s = await api.status();
      setFetched(s.message);
      if (s.enabled) navigate(WEB_ROUTES.login, { replace: true });
    } catch {
      /* still unreachable: stay here */
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[520px] flex-col items-start gap-400 pt-400" data-screen="UnavailableScreen">
      <Wrench size={28} aria-hidden="true" className="text-typography-secondary" />
      <PageHeading>{t('unavailable.heading')}</PageHeading>
      {message ? <p className="text-body text-typography-primary break-words whitespace-pre-wrap">{message}</p> : null}
      <p className="text-body text-typography-secondary">{t('unavailable.body')}</p>
      <Btn type="button" variant="secondary" size="lg" className="min-h-[48px]" loading={checking} disabled={checking} onClick={() => void recheck()}>
        {t('common.retry')}
      </Btn>
    </div>
  );
}
